import React, { useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Link, useHistory, useLocation } from 'react-router-dom'
import * as Yup from 'yup'
import { useForm } from 'react-hook-form'
import { Spinner } from 'reactstrap'
import authApi from '_api/auth'
import SocialButton from '_components/SocialButton'
import useToggle from '_hooks/useToggle'
import { GOOGLE } from '_utils/constant'
import { setUserToken } from '_utils/localData'

import './style.scss'

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .trim()
    .required('Please enter a valid email address.')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Please enter a valid email address.'),
  password: Yup.string().required('Please enter your PIN.'),
})

const LoginError = ({ groupInfo }) => (
  <div className='form-wrap text-center'>
    <h2>Authorization Error</h2>
    <p className='form-sub'>Can&apos;t authorize your account.</p>
    <div className='footer-link'>
      {`${''}Can't authorize, please`}{' '}
      <Link to={{ pathname: '/sign-up', state: { groupInfo } }}>sign up!</Link>
    </div>
  </div>
)

const LoginPage = () => {
  const history = useHistory()
  const location = useLocation()
  const groupInfo = location.state?.groupInfo
  const [isAuthError, setIsAuthError] = useState(false)
  const [showPassword, toggleShowPassword] = useToggle(false)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: true,
    },
  })

  const onSubmit = async (values) => {
    const data = {
      email: values.email.toLowerCase().trim(),
      password: values.password.toLowerCase().trim(),
    }

    try {
      const res = await authApi.login(data)
      setUserToken(res.msgResp.token)
      history.push('/welcome')
    } catch (err) {
      const msg =
        Object.keys(err?.msgResp || {}).length === 1
          ? `Left retries ${err.msgResp.leftRetries}`
          : typeof err?.msgResp === 'string'
          ? err.msgResp
          : 'Login failed'

      if (typeof msg === 'string' && msg.toLowerCase().includes('email')) {
        setError('email', { type: 'manual', message: msg })
      } else {
        setError('password', { type: 'manual', message: msg })
      }
    }
  }

  return (
    <div className='signin-page-v2'>
      <nav className='signin-nav'>
        <Link to='/' className='logo'>
          co<span>·</span>bee
        </Link>
        <div className='nav-right'>
          New to co·bee? <Link to={{ pathname: '/sign-up', state: { groupInfo } }}>Sign up</Link>
        </div>
      </nav>

      <div className='page-body'>
        {isAuthError ? (
          <LoginError groupInfo={groupInfo} />
        ) : (
          <form className='form-wrap' onSubmit={handleSubmit(onSubmit)} noValidate>
            <h2>
              Connect me to
              <br />
              the <em>community!</em>
            </h2>
            <p className='form-sub'>Sign in to browse group buys and manage your orders.</p>

            <div className={`field ${errors.email ? 'has-error' : ''}`}>
              <label htmlFor='email'>Email address</label>
              <input
                id='email'
                type='email'
                placeholder='jane@email.com'
                autoComplete='email'
                {...register('email')}
                className={errors.email ? 'invalid' : ''}
              />
              {errors.email && <div className='field-error'>{errors.email.message}</div>}
            </div>

            <div className={`field ${errors.password ? 'has-error' : ''}`}>
              <div className='field-label-row'>
                <label htmlFor='password'>PIN</label>
                <Link to='/reset-password' className='field-forgot'>
                  Forgot?
                </Link>
              </div>
              <div className='pw-wrap'>
                <input
                  id='password'
                  type={showPassword ? 'text' : 'password'}
                  placeholder='Enter your PIN'
                  autoComplete='current-password'
                  {...register('password')}
                  className={errors.password ? 'invalid' : ''}
                />
                <button type='button' className='pw-toggle' onClick={() => toggleShowPassword()}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {errors.password && <div className='field-error'>{errors.password.message}</div>}
            </div>

            <div className='remember-row'>
              <input type='checkbox' id='remember' {...register('rememberMe')} />
              <label htmlFor='remember'>Keep me signed in</label>
            </div>

            <button type='submit' className='btn-submit' disabled={isSubmitting}>
              {isSubmitting ? <Spinner color='light' size='sm' /> : 'Login'}
            </button>

            <div className='divider'>or</div>

            <SocialButton
              setIsAuthError={setIsAuthError}
              isLogin
              social={GOOGLE}
              renderButton={(renderProps) => (
                <button
                  type='button'
                  className='btn-google'
                  onClick={renderProps.onClick}
                  disabled={renderProps.disabled}
                >
                  <svg width='16' height='16' viewBox='0 0 24 24'>
                    <path
                      fill='#4285F4'
                      d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                    />
                    <path
                      fill='#34A853'
                      d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                    />
                    <path
                      fill='#FBBC05'
                      d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                    />
                    <path
                      fill='#EA4335'
                      d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                    />
                  </svg>
                  Continue with Google
                </button>
              )}
            />

            <p className='footer-link'>
              Not a member?{' '}
              <Link to={{ pathname: '/sign-up', state: { groupInfo } }}>Sign up now</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}

export default LoginPage
